import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  CREATE_WALLET_WEB_CART,
  MAKE_PAYMENT_TRANSACTION,
  ADD_WALLET_USER_PAYMENT_INFO,
  SUBMIT_TT_SAVED_PAYMENT,
  SUBMIT_TT_NON_SAVED_PAYMENT,
  LIST_MY_SAVED_PAYMENT_METHODS,
  DELETE_SAVED_PAYMENT_METHOD,
  GET_NACHA_AGREEMENT,
} from 'requests'
import { PaymentButton } from '../Checkout'
import { RadioInput } from '../../Wizard2/fieldComponents/Form'
import PlusIcon from '@tecuity/sos-icons/react/plus-heavy'
import TrashIcon from '@tecuity/sos-icons/react/trash'
import BankIcon from '@tecuity/sos-icons/react/bank-solid'
import CreditCardIcon from '@tecuity/sos-icons/react/credit-card-solid'
import CrossIcon from '@tecuity/sos-icons/react/cross-heavy'
import { ConfirmDialog } from 'components/Modal/Modal'
import Portal from 'components/Portal/Portal'
import { Spinner } from 'components/Loaders/Loaders'
import { history } from '../../../store'
import { WalletApiIframeModal } from './WalletApiIframeModal'
import './WalletApiSavedPayment.scss'

// Origins the Wallet API IFrame posts messages from.
const WALLET_API_ORIGINS = [
  'https://microservices-uat.cdc.nicusa.com',
  'https://microservices.cdc.nicusa.com',
]

// Extracts a displayable message from a request error. The Payment API rejects with a
// plain string body (e.g. BadRequest("...")), while other endpoints may reject with an
// object, so handle both rather than assuming err.message (which would be undefined for
// the string case and render an empty error box).
export const messageFromError = (err) => {
  if (typeof err === 'string' && err.trim()) return err
  if (err && err.message) return err.message
  if (err && err.internalerror) return err.internalerror
  return 'Your transaction is being processed. To prevent duplicate charges, please wait while we complete your filing. If you continue to experience issues, please contact our office.'
}

// A saved method is a card when its PaymentAccountType is a Credit/Debit/Dual variant;
// Checking/Saving are bank (ACH) accounts. Returns a display label + whether it's a card.
export const describeMethod = (method) => {
  const type = method.PaymentAccountType || ''
  const isCard = /^(Credit|Debit|Dual)/.test(type)
  const lastFour = method.AccountLastFour || ''
  if (isCard) {
    const brand = type.replace(/^(Credit|Debit|Dual)/, '')
    return { isCard: true, label: `${brand || 'Card'} ****${lastFour}` }
  }
  return { isCard: false, label: `${type || 'Account'} ****${lastFour}` }
}

/**
 * Wallet API saved-payment flow:
 * a radio list of saved methods, "Add New Payment Method" buttons, and a Checkout button.
 * Built entirely on AR's Wallet API endpoints.
 */
export const WalletApiSavedPayment = ({
  ProviderType,
  startPayment,
  receiptItemGuids,
  isUsingACH,
}) => {
  const [savedMethods, setSavedMethods] = useState(null) // null = loading
  const [selectedMethod, setSelectedMethod] = useState(null)
  const [iframeUrl, setIframeUrl] = useState(null)
  const [walletId, setWalletId] = useState(null)
  const [webCartNum, setWebCartNum] = useState(null)
  const [addingType, setAddingType] = useState(null) // 'card' | 'ach' | null
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [deletingMethod, setDeletingMethod] = useState(null)
  // Pending ACH checkout awaiting Nacha agreement acceptance in the IFrame.
  const [pendingNacha, setPendingNacha] = useState(null)
  // Synchronous in-flight flag for the add flow. `disabled={addingType!==null}` can be raced by a fast
  // double-click (before the disabled state re-renders), which fires addPaymentMethod twice and creates
  // two web carts. This ref is set/read synchronously so the second call bails immediately.
  const addingRef = useRef(false)

  const closeIframe = () => setIframeUrl(null)

  // Dismissing the IFrame (its close button/overlay) or a Cancel message from it must also
  // stop the checkout spinner and drop any pending ACH agreement. Otherwise closing the
  // Nacha agreement modal without agreeing leaves isLoading true, so the spinner spins
  // forever and the checkout UI stays locked.
  const dismissIframe = () => {
    closeIframe()
    setPendingNacha(null)
    setIsLoading(false)
  }

  // Ends the checkout spinner and surfaces an error from the payment flow. On success the
  // flow redirects to the processpayment route, so the spinner is only cleared on failure.
  const failPayment = (err) => {
    setIsLoading(false)
    setError(messageFromError(err))
    console.error(err)
  }

  const loadSavedMethods = useCallback(() => {
    LIST_MY_SAVED_PAYMENT_METHODS(
      (methods) => setSavedMethods(methods || []),
      (err) => {
        setSavedMethods([])
        setError(messageFromError(err))
        console.error(err)
      }
    )
  }, [])

  useEffect(() => {
    loadSavedMethods()
  }, [loadSavedMethods])

  // Build the add IFrame for a new payment method: create the web cart, then fetch the
  // IFrame source + walletId (reuses the existing wallet add/cache flow).
  const addPaymentMethod = (paymentType) => {
    // Bail if an add is already in flight -- prevents a rapid double-click from creating two web carts.
    if (addingRef.current) return
    addingRef.current = true

    setError(null)
    setAddingType(paymentType)
    CREATE_WALLET_WEB_CART(
      { ITEMIDS: receiptItemGuids },
      (refNum) => {
        setWebCartNum(refNum)
        localStorage.setItem('webCartNum', refNum)
        MAKE_PAYMENT_TRANSACTION(
          { webCartNum: refNum, paymentType, achType: 'TBD' },
          (res) => {
            setWalletId(res.walletId)
            setIframeUrl(res.iFrameSource)
            setAddingType(null)
            addingRef.current = false
          },
          (err) => {
            setAddingType(null)
            addingRef.current = false
            setError(messageFromError(err))
            console.error(err)
          }
        )
      },
      (err) => {
        setAddingType(null)
        addingRef.current = false
        setError(messageFromError(err))
        console.error(err)
      }
    )
  }

  // Submit the saved payment for a given cart, optionally with a Nacha agreement guid
  // (required for ACH), then continue through the saved processpayment route.
  const submitSavedPayment = (refNum, nachaAgreementGuid) => {
    SUBMIT_TT_SAVED_PAYMENT(
      refNum,
      { WalletId: selectedMethod.WalletId, PaymentMethodId: selectedMethod.Id },
      nachaAgreementGuid,
      () => {
        // Leaving the wizard for the processpayment route is intentional here, so bypass the
        // wizard's "exit this form?" navigation prompt (one-shot flag read by Wizard.js).
        window.ALLOW_BYPASS_WIZARD_PROMPT = true
        history.push(`/processpayment/walletapisaved/?ref=${refNum}`)
      },
      failPayment
    )
  }

  // Charge the selected saved method: create a fresh web cart for the current items.
  // Cards charge immediately. ACH first requires the user to accept a Nacha agreement
  // in an IFrame; the returned AgreementGuid is then sent with the payment.
  const checkoutWithSavedMethod = () => {
    const { isCard } = describeMethod(selectedMethod)
    CREATE_WALLET_WEB_CART(
      { ITEMIDS: receiptItemGuids },
      (refNum) => {
        if (isCard) {
          submitSavedPayment(refNum)
          return
        }

        // ACH: fetch a Nacha agreement and present it for acceptance.
        GET_NACHA_AGREEMENT(
          refNum,
          {
            WalletId: selectedMethod.WalletId,
            PaymentAccountId: selectedMethod.Id,
            AchAccountLastFour: selectedMethod.AccountLastFour,
          },
          (agreement) => {
            setPendingNacha({
              refNum,
              agreementGuid: agreement.AgreementGuid,
              walletId: selectedMethod.WalletId,
              paymentMethodId: selectedMethod.Id,
            })
            setIframeUrl(agreement.IFrameUrl)
          },
          failPayment
        )
      },
      failPayment
    )
  }

  // Guest (non-saved) checkout: the cache IFrame already collected the payment, so submit
  // immediately and continue through the non-saved processpayment route.
  const checkoutWithNonSavedMethod = (refNum) => {
    SUBMIT_TT_NON_SAVED_PAYMENT(
      refNum,
      () => {
        // Intentional wizard exit -> bypass the "exit this form?" prompt.
        window.ALLOW_BYPASS_WIZARD_PROMPT = true
        history.push(`/processpayment/walletapinonsaved/?ref=${refNum}`)
      },
      (err) => {
        setError(messageFromError(err))
        console.error(err)
      }
    )
  }

  useEffect(() => {
    const handleMessage = (event) => {
      if (!WALLET_API_ORIGINS.includes(event.origin)) return

      if (event.data === 'PaymentAccountAdded') {
        // Method saved to the wallet -> register it, then refresh the list. Pass the account
        // ids we already have loaded so the server can identify which account is the new one
        // (the Wallet API doesn't tell us the added account's id directly).
        closeIframe()
        ADD_WALLET_USER_PAYMENT_INFO(
          walletId,
          (savedMethods || []).map((m) => m.Id),
          () => loadSavedMethods(),
          (err) => {
            setError(messageFromError(err))
            console.error(err)
          }
        )
      }

      if (event.data === 'PaymentAccountCached') {
        // Guest checkout -> pay immediately with the cached method.
        closeIframe()
        startPayment(() => checkoutWithNonSavedMethod(webCartNum))
      }

      if (event.data === 'NachaAgreementAccepted' && pendingNacha) {
        // ACH debit authorized -> charge the saved account with the agreement guid.
        closeIframe()
        const nacha = pendingNacha
        setPendingNacha(null)
        SUBMIT_TT_SAVED_PAYMENT(
          nacha.refNum,
          { WalletId: nacha.walletId, PaymentMethodId: nacha.paymentMethodId },
          nacha.agreementGuid,
          () => {
            // Intentional wizard exit -> bypass the "exit this form?" prompt.
            window.ALLOW_BYPASS_WIZARD_PROMPT = true
            history.push(`/processpayment/walletapisaved/?ref=${nacha.refNum}`)
          },
          failPayment
        )
      }

      if (event.data === 'Cancel') {
        // User backed out of the ACH agreement -> stop the checkout spinner.
        dismissIframe()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
    // Re-bind when these change so the handler reads current ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId, webCartNum, pendingNacha, savedMethods, loadSavedMethods])

  const handleCheckout = () => {
    if (!selectedMethod) return
    setError(null)
    setIsLoading(true)
    // startPayment only kicks off the async charge; keep the spinner up until the flow
    // redirects on success or clears it via failPayment / Cancel on failure.
    startPayment(checkoutWithSavedMethod)
  }

  const deleteMethod = (method) => {
    // Optimistic removal; restore by reloading on failure.
    setSavedMethods((methods) => (methods || []).filter((m) => m.Id !== method.Id))
    if (selectedMethod && selectedMethod.Id === method.Id) {
      setSelectedMethod(null)
    }
    DELETE_SAVED_PAYMENT_METHOD(
      method.WalletId,
      method.Id,
      () => {},
      (err) => {
        setError(messageFromError(err))
        console.error(err)
        loadSavedMethods()
      }
    )
  }

  const renderAddButton = (paymentType, label) => (
    <button
      type="button"
      className="wallet-add-btn"
      disabled={addingType !== null}
      onClick={() => addPaymentMethod(paymentType)}
    >
      {addingType === paymentType ? (
        <React.Fragment>
          <Spinner size="small" color="rgba(255,255,255,.6)" /> Adding Payment Method
        </React.Fragment>
      ) : (
        <React.Fragment>
          <PlusIcon /> {label}
        </React.Fragment>
      )}
    </button>
  )

  return (
    <div className="walletapi-saved-payments" key={ProviderType}>
      {savedMethods === null ? (
        <Spinner color="#7792d0" className="checkout-loader" size="small" />
      ) : (
        <React.Fragment>
          {savedMethods.length > 0 && (
            <div className="wizard">
              <RadioInput
                label="Select or add a payment method to checkout."
                ownsLabel
                value={selectedMethod ? selectedMethod.Id : ''}
                onChange={(id) =>
                  setSelectedMethod(savedMethods.find((m) => m.Id === id) || null)
                }
                options={savedMethods.map((method) => {
                  const { isCard, label } = describeMethod(method)
                  return {
                    value: method.Id,
                    label: (
                      <span className="wallet-method-label">
                        {isCard ? <CreditCardIcon alt="Credit Card icon" /> : <BankIcon />}
                        {label}
                        <button
                          className="wallet-trash-button"
                          type="button"
                          onClick={() => setDeletingMethod(method)}
                        >
                          <TrashIcon />
                        </button>
                      </span>
                    ),
                  }
                })}
              />
            </div>
          )}

          {renderAddButton('card', 'Add Credit or Debit Card')}
          {isUsingACH && renderAddButton('ach', 'Add ACH')}

          {error && (
            <p className="wallet-payment-error" role="alert">
              <CrossIcon /> <span>{error}</span>
            </p>
          )}

          {selectedMethod && (
            <PaymentButton
              onClick={handleCheckout}
              disabled={!selectedMethod || isLoading}
              loading={isLoading}
              bypassLoadingStyles
            >
              {isLoading ? 'Processing' : 'Checkout'}
            </PaymentButton>
          )}

          {/* Gated on isLoading alone (not selectedMethod) so it appears for every in-flight
              payment -- card, ACH, and while the flow redirects on success. */}
          {isLoading && (
            <p className="wallet-processing-message" role="status">
              Your payment is processing - Do not close your browser or click the back button
            </p>
          )}

        </React.Fragment>
      )}

      <WalletApiIframeModal iframeUrl={iframeUrl} onClose={dismissIframe} />

      <Portal isOpened={!!deletingMethod}>
        <ConfirmDialog
          closeModal={() => setDeletingMethod(null)}
          onModalClosed={() => {}}
          message="Are you sure you want to delete this payment method? This cannot be undone."
          buttons={[
            { label: 'No', onClick: () => {}, className: 'btn-default' },
            {
              label: 'Yes',
              onClick: () => deleteMethod(deletingMethod),
              className: 'btn-danger',
            },
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
    </div>
  )
}
